import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '@store/helpers';
import { selectItemType } from '@src/types';
import {
  ComplianceLogEntry,
  CreateUploadSessionRequest,
  CreatePersonnelNoteRequest,
  CreatePersonnelRequest,
  CredentialKind,
  CredentialRecord,
  CredentialRecordsRequest,
  MonthlyCheck,
  NoteAuditEntry,
  PayerCompliance,
  PersonnelNote,
  PhoneUploadInfo,
  ReadDocumentRequest,
  ReadDocumentResult,
  SaveCredentialRecordRequest,
  SaveMonthlyCheckRequest,
  UpdatePersonnelNoteRequest,
  UploadSession,
  UploadSessionStatus,
  DuplicateCheckRequest,
  DuplicateMatch,
  ImageUploadRequest,
  ImageUploadResponse,
  PageResponse,
  PersonnelListItem,
  PersonnelListRequest,
  PersonnelProfile,
  RevealRequest,
  RevealResponse,
  UpdatePersonnelRequest,
} from '@src/types/personnel';
import { BIG_TABLE_DEFAULT_PAGE_SIZE } from '@utils/constants';
import { contractQueryFn } from './credentialingContract';
import {
  toCredentialRecords,
  toLegacyPersonnelCreate,
  toLegacyPersonnelUpdate,
  toPersonnelProfile,
  toLegacyRecordSave,
} from './legacyPersonnel';
import { complianceMocks } from './mocks/complianceMocks';
import { credentialMocks } from './mocks/credentialMocks';
import { notesMocks } from './mocks/notesMocks';
import { personnelMocks } from './mocks/personnelMocks';
import { uploadSessionMocks } from './mocks/uploadSessionMocks';

const RECORD_PATHS: Record<CredentialKind, string> = {
  identification: 'identification',
  permit: 'permits',
  screening: 'screenings',
  training: 'certifications',
};

const recordsTag = (kind: CredentialKind, personnelId: number) => ({
  type: 'CredentialRecords' as const,
  id: `${kind}-${personnelId}`,
});

// multipart: the record as a JSON part, then each file with its slot
const filesFormData = (
  record: Record<string, unknown>,
  files: Array<{ file: File; slot?: string; source?: string }>
): FormData => {
  const body = new FormData();
  body.append(
    'record',
    new Blob(
      [
        JSON.stringify({
          ...record,
          files: files.map(({ slot, source }) => ({ slot, source })),
        }),
      ],
      { type: 'application/json' }
    )
  );
  files.forEach(({ file }) => body.append('files', file));
  return body;
};

export const personnelApi = createApi({
  reducerPath: 'Personnel',
  tagTypes: [
    'Personnel',
    'CredentialRecords',
    'PayerCompliance',
    'ComplianceLog',
    'PersonnelNotes',
  ],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    // Contractor Portal Personnel contract (src/types/personnel.ts)
    getPersonnelList: builder.mutation<
      PageResponse<PersonnelListItem>,
      PersonnelListRequest
    >({
      queryFn: contractQueryFn(
        ({ params, body = {} }) => ({
          url: '/v1/personnel/personnel',
          method: 'POST',
          params: {
            page: 0,
            size: BIG_TABLE_DEFAULT_PAGE_SIZE,
            sortBy: 'lastName',
            sortDir: 'asc',
            ...params,
          },
          body,
        }),
        personnelMocks.list
      ),
    }),
    checkPersonnelDuplicate: builder.mutation<
      Array<DuplicateMatch>,
      DuplicateCheckRequest
    >({
      queryFn: contractQueryFn(
        (body) => ({
          url: '/v1/personnel/personnel/duplicates',
          method: 'POST',
          body,
        }),
        personnelMocks.checkDuplicate
      ),
    }),
    getPersonnelProfile: builder.query<PersonnelProfile, { id: number }>({
      queryFn: contractQueryFn(
        ({ id }) => ({ url: `/v1/personnel/personnel/${id}` }),
        personnelMocks.getProfile,
        { fromServer: toPersonnelProfile }
      ),
      providesTags: (_result, _error, { id }) => [{ type: 'Personnel', id }],
    }),
    createPersonnel: builder.mutation<PersonnelProfile, CreatePersonnelRequest>(
      {
        queryFn: contractQueryFn(
          (body) => ({
            url: '/v1/personnel/personnel/create',
            method: 'POST',
            body,
          }),
          personnelMocks.create,
          { toServer: toLegacyPersonnelCreate }
        ),
      }
    ),
    updatePersonnel: builder.mutation<PersonnelProfile, UpdatePersonnelRequest>(
      {
        queryFn: contractQueryFn(
          ({ id, changes, note }) => ({
            url: `/v1/personnel/personnel/update/${id}`,
            method: 'PUT',
            body: { ...changes, note },
          }),
          personnelMocks.update,
          { toServer: toLegacyPersonnelUpdate }
        ),
        // a status change also writes the compliance log (WF-D4 card 3)
        invalidatesTags: (_result, error, { id }) =>
          error
            ? []
            : [
                { type: 'Personnel', id },
                { type: 'ComplianceLog', id },
                { type: 'PayerCompliance', id },
                { type: 'PersonnelNotes', id },
              ],
      }
    ),
    // same shape as globalApi.uploadImage so uploadImageByPath can drive it;
    // the stored path it returns goes into profilePictureUrl
    uploadPersonnelPicture: builder.mutation<
      ImageUploadResponse,
      ImageUploadRequest
    >({
      queryFn: contractQueryFn<ImageUploadRequest, ImageUploadResponse>(
        (args) => args,
        personnelMocks.uploadPicture
      ),
    }),
    // logged server side; call with initiate(arg, { track: false }) so the
    // revealed value never lands in the store
    revealPersonnelValue: builder.mutation<RevealResponse, RevealRequest>({
      queryFn: contractQueryFn(
        ({ personnelId, ...body }) => ({
          url: `/v1/personnel/personnel/${personnelId}/reveal`,
          method: 'POST',
          body,
        }),
        personnelMocks.reveal
      ),
    }),
    // WF-D2 credential records, one set of endpoints per kind
    getCredentialRecords: builder.query<
      Array<CredentialRecord>,
      CredentialRecordsRequest
    >({
      queryFn: contractQueryFn(
        ({ kind, personnelId }) => ({
          url: `/v1/personnel/${RECORD_PATHS[kind]}/personnel/${personnelId}`,
        }),
        credentialMocks.list,
        {
          fromServer: (data, { kind, personnelId }) =>
            toCredentialRecords(data, kind, personnelId),
        }
      ),
      providesTags: (_result, _error, { kind, personnelId }) => [
        recordsTag(kind, personnelId),
      ],
    }),
    // barcode first for an ID, OCR otherwise; nothing is saved
    readCredentialDocument: builder.mutation<
      ReadDocumentResult,
      ReadDocumentRequest
    >({
      queryFn: contractQueryFn(
        ({ files, kind, phoneFiles, type }) => ({
          url: `/v1/personnel/${RECORD_PATHS[kind]}/read`,
          method: 'POST',
          body: filesFormData({ phoneFiles, type }, files),
        }),
        credentialMocks.read
      ),
    }),
    getCredentialSelect: builder.query<Array<selectItemType>, string>({
      queryFn: contractQueryFn(
        (url) => ({ url }),
        () => []
      ),
    }),
    saveCredentialRecord: builder.mutation<
      CredentialRecord,
      SaveCredentialRecordRequest
    >({
      queryFn: contractQueryFn(
        ({ id, kind, newFiles, ...record }) => ({
          url: `/v1/personnel/${RECORD_PATHS[kind]}/${id ? `update/${id}` : 'create'}`,
          method: id ? 'PUT' : 'POST',
          body: filesFormData(record, newFiles),
        }),
        credentialMocks.save,
        {
          fromServer: (data, { kind, personnelId }) =>
            toCredentialRecords([data], kind, personnelId)[0],
          toServer: toLegacyRecordSave,
        }
      ),
      // the profile ID number and dates can follow a saved record
      invalidatesTags: (_result, error, { kind, personnelId }) =>
        error
          ? []
          : [
              recordsTag(kind, personnelId),
              { type: 'Personnel', id: personnelId },
              { type: 'PayerCompliance', id: personnelId },
              { type: 'ComplianceLog', id: personnelId },
            ],
    }),
    saveMonthlyCheck: builder.mutation<MonthlyCheck, SaveMonthlyCheckRequest>({
      queryFn: contractQueryFn(
        ({ newFiles, screeningId, ...check }) => ({
          url: `/v1/personnel/screenings/${screeningId}/monthly-checks/create`,
          method: 'POST',
          body: filesFormData(check, newFiles),
        }),
        credentialMocks.saveMonthlyCheck
      ),
      invalidatesTags: (_result, error, { personnelId }) =>
        error
          ? []
          : [
              recordsTag('screening', personnelId),
              { type: 'PayerCompliance', id: personnelId },
            ],
    }),
    // WF-D3 / WF-D5: every linked payer's templates, worked out by Auxilium
    getPayerCompliance: builder.query<
      Array<PayerCompliance>,
      { personnelId: number }
    >({
      queryFn: contractQueryFn(
        ({ personnelId }) => ({
          url: `/v1/personnel/personnel/${personnelId}/payer-compliance`,
        }),
        complianceMocks.payerCompliance,
        { whenMissing: [] }
      ),
      providesTags: (_result, _error, { personnelId }) => [
        { type: 'PayerCompliance', id: personnelId },
      ],
    }),
    // newest entry first, projected end dates flagged
    getComplianceLog: builder.query<
      Array<ComplianceLogEntry>,
      { personnelId: number }
    >({
      queryFn: contractQueryFn(
        ({ personnelId }) => ({
          url: `/v1/personnel/personnel/${personnelId}/compliance-log`,
        }),
        complianceMocks.complianceLog,
        { whenMissing: [] }
      ),
      providesTags: (_result, _error, { personnelId }) => [
        { type: 'ComplianceLog', id: personnelId },
      ],
    }),
    // WF-D6 contractor notes: text only, edited by their author, never deleted
    getPersonnelNotes: builder.query<
      Array<PersonnelNote>,
      { personnelId: number }
    >({
      queryFn: contractQueryFn(
        ({ personnelId }) => ({
          url: `/v1/personnel/personnel/${personnelId}/notes`,
        }),
        notesMocks.list,
        { whenMissing: [] }
      ),
      providesTags: (_result, _error, { personnelId }) => [
        { type: 'PersonnelNotes', id: personnelId },
      ],
    }),
    createPersonnelNote: builder.mutation<
      PersonnelNote,
      CreatePersonnelNoteRequest
    >({
      queryFn: contractQueryFn(
        ({ note, personnelId }) => ({
          url: `/v1/personnel/personnel/${personnelId}/notes/create`,
          method: 'POST',
          body: note,
        }),
        notesMocks.create
      ),
      invalidatesTags: (_result, error, { personnelId }) =>
        error ? [] : [{ type: 'PersonnelNotes', id: personnelId }],
    }),
    updatePersonnelNote: builder.mutation<
      PersonnelNote,
      UpdatePersonnelNoteRequest
    >({
      queryFn: contractQueryFn(
        ({ noteId, personnelId, text }) => ({
          url: `/v1/personnel/personnel/${personnelId}/notes/update/${noteId}`,
          method: 'PUT',
          body: { text },
        }),
        notesMocks.update
      ),
      invalidatesTags: (_result, error, { personnelId }) =>
        error ? [] : [{ type: 'PersonnelNotes', id: personnelId }],
    }),
    getNoteAudit: builder.query<
      Array<NoteAuditEntry>,
      { personnelId: number; noteId: number }
    >({
      queryFn: contractQueryFn(
        ({ noteId, personnelId }) => ({
          url: `/v1/personnel/personnel/${personnelId}/notes/${noteId}/audit`,
        }),
        notesMocks.audit
      ),
    }),
    // US-D2.32 upload from phone: the computer side
    createUploadSession: builder.mutation<
      UploadSession,
      CreateUploadSessionRequest
    >({
      queryFn: contractQueryFn(
        (body) => ({
          url: '/v1/personnel/upload-sessions/create',
          method: 'POST',
          body,
        }),
        uploadSessionMocks.create
      ),
    }),
    sendUploadLink: builder.mutation<
      unknown,
      { id: string; mobileNumber: string }
    >({
      queryFn: contractQueryFn(
        ({ id, mobileNumber }) => ({
          url: `/v1/personnel/upload-sessions/${id}/send-link`,
          method: 'POST',
          body: { mobileNumber },
        }),
        uploadSessionMocks.sendLink
      ),
    }),
    // polled while the popup is open
    getUploadSession: builder.query<UploadSessionStatus, { id: string }>({
      queryFn: contractQueryFn(
        ({ id }) => ({ url: `/v1/personnel/upload-sessions/${id}` }),
        uploadSessionMocks.status
      ),
    }),
    // ends the link when the popup is done or the panel closes
    endUploadSession: builder.mutation<unknown, { id: string }>({
      queryFn: contractQueryFn(
        ({ id }) => ({
          url: `/v1/personnel/upload-sessions/${id}`,
          method: 'DELETE',
        }),
        uploadSessionMocks.end
      ),
    }),
    // the phone side: public, by the link's token, behind the six-digit code
    getPhoneUploadStatus: builder.query<
      Pick<UploadSessionStatus, 'expiresAt' | 'status'>,
      { token: string }
    >({
      queryFn: contractQueryFn(
        ({ token }) => ({ url: `/v1/public/upload-sessions/${token}` }),
        uploadSessionMocks.publicStatus
      ),
    }),
    verifyPhoneUpload: builder.mutation<
      PhoneUploadInfo,
      { token: string; code: string }
    >({
      queryFn: contractQueryFn(
        ({ code, token }) => ({
          url: `/v1/public/upload-sessions/${token}/verify`,
          method: 'POST',
          body: { code },
        }),
        uploadSessionMocks.verify
      ),
    }),
    uploadPhoneFiles: builder.mutation<
      { uploaded: number },
      { token: string; body: FormData }
    >({
      queryFn: contractQueryFn(
        ({ body, token }) => ({
          url: `/v1/public/upload-sessions/${token}/files`,
          method: 'POST',
          body,
        }),
        uploadSessionMocks.upload
      ),
    }),
    // legacy endpoints, still used by the admin Personnel screens
    getPersonnelData: builder.mutation({
      query: ({
        page,
        size,
        body = {},
        params = { sortBy: 'expirationDate', sortDir: 'desc' },
      }) => ({
        url: `/v1/personnel/personnel?page=${page}&size=${size}`,
        method: 'POST',
        body,
        params,
      }),
    }),
    createOrUpdatePersonnelData: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/personnel/personnel/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
    getScreeningTypeSelect: builder.query<Array<selectItemType>, {}>({
      query: () => ({
        url: `/v1/personnel/screenings-types/select`,
      }),
    }),
    getPersonnelIdentificationTableData: builder.query({
      query: ({ id, sortBy = 'id', sortDir = 'asc' }) => ({
        url: `/v1/personnel/identification/personnel/${id}?&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getPersonnelIdentificationById: builder.query({
      query: ({ id }) => ({
        url: `/v1/personnel/identification/${id}`,
      }),
    }),
    createOrUpdatePersonnelIdentification: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/personnel/identification/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
    getPersonnelCertificationTableData: builder.query({
      query: ({ id, sortBy = 'id', sortDir = 'asc' }) => ({
        url: `/v1/personnel/certifications/personnel/${id}?&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getPersonnelCertificationById: builder.query({
      query: ({ id }) => ({
        url: `/v1/personnel/certifications/${id}`,
      }),
    }),
    createOrUpdatePersonnelCertification: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/personnel/certifications/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
    getPersonnelScreeningTableData: builder.query({
      query: ({ id, sortBy = 'id', sortDir = 'desc' }) => ({
        url: `/v1/personnel/screenings/personnel/${id}?&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getPersonnelScreeningById: builder.query({
      query: ({ id }) => ({
        url: `/v1/personnel/screenings/${id}`,
      }),
    }),
    createOrUpdatePersonnelScreening: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/personnel/screenings/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
    getPersonnelPermitTableData: builder.query({
      query: ({ id, sortBy = 'id', sortDir = 'desc' }) => ({
        url: `/v1/personnel/permits/personnel/${id}?&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getPersonnelPermitById: builder.query({
      query: ({ id }) => ({
        url: `/v1/personnel/permits/${id}`,
      }),
    }),
    createOrUpdatePersonnelPermit: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/personnel/permits/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
    getPersonnelServiceDateTableData: builder.query({
      query: ({ id, sortBy = 'id', sortDir = 'desc' }) => ({
        url: `/v1/personnel/service-dates/personnel/${id}?&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getPersonnelServiceDateById: builder.query({
      query: ({ id }) => ({
        url: `/v1/personnel/service-dates/${id}`,
      }),
    }),
    createOrUpdatePersonnelServiceDate: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/personnel/service-dates/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
  }),
});

export const {
  useGetPersonnelListMutation,
  useGetCredentialSelectQuery,
  useCheckPersonnelDuplicateMutation,
  useGetPersonnelProfileQuery,
  useCreatePersonnelMutation,
  useUpdatePersonnelMutation,
  useUploadPersonnelPictureMutation,
  useGetCredentialRecordsQuery,
  useLazyGetCredentialRecordsQuery,
  useGetPayerComplianceQuery,
  useGetComplianceLogQuery,
  useGetPersonnelNotesQuery,
  useCreatePersonnelNoteMutation,
  useUpdatePersonnelNoteMutation,
  useGetNoteAuditQuery,
  useCreateUploadSessionMutation,
  useSendUploadLinkMutation,
  useGetUploadSessionQuery,
  useEndUploadSessionMutation,
  useGetPhoneUploadStatusQuery,
  useVerifyPhoneUploadMutation,
  useUploadPhoneFilesMutation,
  useReadCredentialDocumentMutation,
  useSaveCredentialRecordMutation,
  useSaveMonthlyCheckMutation,
  useGetPersonnelDataMutation,
  useCreateOrUpdatePersonnelDataMutation,
  useLazyGetPersonnelIdentificationTableDataQuery,
  useLazyGetPersonnelIdentificationByIdQuery,
  useCreateOrUpdatePersonnelIdentificationMutation,
  useLazyGetPersonnelCertificationTableDataQuery,
  useLazyGetPersonnelCertificationByIdQuery,
  useCreateOrUpdatePersonnelCertificationMutation,
  useLazyGetPersonnelScreeningTableDataQuery,
  useLazyGetPersonnelScreeningByIdQuery,
  useCreateOrUpdatePersonnelScreeningMutation,
  useLazyGetPersonnelPermitTableDataQuery,
  useLazyGetPersonnelPermitByIdQuery,
  useCreateOrUpdatePersonnelPermitMutation,
  useLazyGetPersonnelServiceDateTableDataQuery,
  useLazyGetPersonnelServiceDateByIdQuery,
  useCreateOrUpdatePersonnelServiceDateMutation,
  useLazyGetScreeningTypeSelectQuery,
} = personnelApi;
